import {
  Layout,
  Photo,
  PhotoMarker,
  Unit,
  MarkerAttributes,
  CreateAreaTagData,
  RawPhotoMarker,
} from '../types/types';
import { GET, POST, PATCH, DELETE } from '@/shared/api/requests';
import { getCsrfToken } from '@/utils/csrf';
import axios from 'axios';

// Helper for legacy Django endpoints that live outside /api/v1
const getBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace('/api/v1', '');
  }
  return '';
};

const ajaxGet = async (url: string) => {
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  const response = await axios.get(fullUrl, {
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    }
  });
  return response.data;
};

const ajaxPost = async (url: string, data: unknown) => {
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  const isFormData = data instanceof FormData;
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRFToken': getCsrfToken() ?? '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
  };
  
  const response = await axios.post(fullUrl, data, {
    withCredentials: true,
    headers,
  });
  return response.data;
};

const ajaxPatch = async (url: string, data: unknown) => {
  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  const isFormData = data instanceof FormData;
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRFToken': getCsrfToken() ?? '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
  };
  
  const response = await axios.patch(fullUrl, data, {
    withCredentials: true,
    headers,
  });
  return response.data;
};

const getLayouts = async (projectId: string, type?: string): Promise<Layout[]> => {
  const url = type 
    ? `/projects/${projectId}/layouts/?type=${type}`
    : `/projects/${projectId}/layouts/`;
  const response = await GET(url);
  const layouts = response.data.layouts || [];
  return layouts;
};

const getAreaTags = async (layoutId: string): Promise<Unit[]> => {
  return await ajaxGet(`/ironview360/ajax/get-v3-area-tags-for-layout/?layout_id=${layoutId}`);
};

const getPhotoMarkers = async (layoutId: string): Promise<PhotoMarker[]> => {
  try {
    const response = await GET(`/layouts/${layoutId}/markers/`);
    const rawMarkers: RawPhotoMarker[] = response.data.markers || [];

    return rawMarkers.map((marker: RawPhotoMarker) => ({
      id: marker.index,
      number: marker.index,
      x: marker.x,
      y: marker.y,
      photos: marker.photos || [],
      width: marker.width,
      height: marker.height,
      fontSize: marker.font_size,
    }));
  } catch (error) {
    console.error('Error fetching photo markers:', error);
    throw error;
  }
};

const getAvailablePhotos = async (layoutId: string): Promise<Photo[]> => {
  return (await GET(`/layouts/${layoutId}/photos/`)).data;
};

// Deprecated: showPhoto function removed - use getPhoto360 instead

const getPhoto360 = async (photoId: number) => {
  try {
    const response = await ajaxGet(`/ironview360/photo/${photoId}/360/`);
    return response;
  } catch (error) {
    console.error('❌ Error fetching 360 photo:', error);
    throw error;
  }
};

const getPhotoHotspots = async (photoId: number) => {
  return await ajaxGet(`/ironview360/photo/${photoId}/hotspots/`);
};

const getHotspotOverlays = async (hotspotId: number) => {
  return await ajaxGet(`/ironview360/hotspots/${hotspotId}/overlays/`);
};

const createAreaTag = async (data: CreateAreaTagData) => {
  const formData = new FormData();
  const jsonData = {
    name: data.name,
    layout_id: data.layout_id,
    photo_ids: data.photo_ids,
    coordinates: data.coordinates,
  };

  formData.append('data', JSON.stringify(jsonData));
  return await ajaxPost('/ironview360/ajax/create-area-tag/', formData);
};

const savePhotoMarkers = async (
  layoutId: string,
  markers: PhotoMarker[],
  markerAttributes: MarkerAttributes
) => {
  const markersData = markers.map((marker) => ({
    layout_id: layoutId,
    index: marker.number,
    x: marker.x,
    y: marker.y,
    width: marker.width,
    height: marker.height,
    font_size: marker.fontSize,
  }));

  return (
    await POST(
      `/layouts/${layoutId}/markers/`,
      {
        markers: markersData,
        markerAttributes: markerAttributes,
      },
      {
        'Content-Type': 'application/json',
      }
    )
  ).data;
};

const createHotspot = async (
  photoId: number,
  position: {
    pitch: number;
    yaw: number;
    hfov: number;
  }
) => {
  return await ajaxPost(`/ironview360/photo/${photoId}/hotspots/`, position);
};

const createNote = async (noteData: {
  hotspot: number;
  title: string;
  subject: string;
  description: string;
  reminder_date?: string | null;
  reminder_time?: string | null;
  reminder_message?: string;
  visible_to?: string[];
}) => {
  return (
    await POST(`/notes/`, noteData, {
      'Content-Type': 'application/json',
    })
  ).data;
};

const updateNote = async (
  noteId: number,
  noteData: {
    title: string;
    subject: string;
    description: string;
    reminder_date?: string | null;
    reminder_time?: string | null;
    reminder_message?: string;
    visible_to?: string[];
  },
  projectId: string
) => {
  return (
    await PATCH(`/notes/${noteId}/?project_id=${projectId}`, noteData, {
      'Content-Type': 'application/json',
    })
  ).data;
};

const deleteNote = async (noteId: number, projectId: string) => {
  return (await DELETE(`/notes/${noteId}/?project_id=${projectId}`)).data;
};

const deleteOverlay = async (_hotspotId: number, overlayId: number) => {
  return (await DELETE(`/overlays/${overlayId}/`)).data;
};

const deleteHotspot = async (hotspotId: number, projectId: string) => {
  return (await DELETE(`/hotspots/${hotspotId}/?project_id=${projectId}`)).data;
};

const updateHotspot = async (
  hotspotId: number,
  data: {
    title?: string;
    pitch?: number;
    yaw?: number;
    hfov?: number;
  },
  projectId: string
) => {
  return (
    await PATCH(`/hotspots/${hotspotId}/?project_id=${projectId}`, data, {
      'Content-Type': 'application/json',
    })
  ).data;
};

const getNotes = async (hotspotId: number) => {
  return (await GET(`/notes/?hotspot=${hotspotId}`)).data;
};

const createNoteAttachment = async (noteId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('note', noteId.toString());

  return (await POST(`/note-attachments/`, formData)).data;
};

const getProjectUsers = async (projectId: string) => {
  return (await GET(`/project-users/?project_id=${projectId}`)).data;
};

const getNote = async (noteId: number) => {
  return (await GET(`/notes/${noteId}/`)).data;
};

const uploadOverlay = async (
  hotspotId: number,
  file: File,
  title?: string,
  groupId?: number
) => {
  const formData = new FormData();
  formData.append('photo', file);
  if (title) {
    formData.append('title', title);
  }
  if (groupId !== undefined) {
    formData.append('group_id', String(groupId));
  }

  return await ajaxPost(`/ironview360/hotspots/${hotspotId}/overlays/`, formData);
};

const createLabel = async (labelData: {
  hotspot: number;
  title: string;
  description: string;
  color?: string;
}) => {
  return (
    await POST(`/labels/`, labelData, {
      'Content-Type': 'application/json',
    })
  ).data;
};

const getLabels = async (hotspotId: number) => {
  return (await GET(`/labels/?hotspot=${hotspotId}`)).data;
};

const uploadOverlayToHotspot = async (
  hotspotId: number,
  file: File,
  title?: string,
  groupId?: number
) => {
  const formData = new FormData();
  formData.append('photo', file);
  if (title) {
    formData.append('title', title);
  }
  if (groupId !== undefined) {
    formData.append('group_id', String(groupId));
  }

  return await ajaxPost(`/ironview360/hotspots/${hotspotId}/overlays/`, formData);
};

const updateOverlay = async (
  hotspotId: number,
  overlayId: number,
  data: { title?: string; photo?: File; group_id?: number | null; new_group_name?: string }
) => {
  const formData = new FormData();

  formData.append('overlay_id', overlayId.toString());

  if (data.title !== undefined) {
    formData.append('title', data.title);
  }

  if (data.photo) {
    formData.append('photo', data.photo);
  }
  if (data.group_id !== undefined) {
    formData.append('group_id', data.group_id === null ? '' : String(data.group_id));
  }
  if (data.new_group_name) {
    formData.append('new_group_name', data.new_group_name);
  }

  return await ajaxPatch(`/ironview360/hotspots/${hotspotId}/overlays/`, formData);
};

const getHotspotOverlayGroups = async (hotspotId: number) => {
  const response = await ajaxGet(`/ironview360/hotspots/${hotspotId}/overlay-groups/`);
  return {
    groups: Array.isArray(response)
      ? response
      : response.results || response.groups || [],
  };
};

const createHotspotOverlayGroup = async (
  hotspotId: number,
  data: { name?: string }
) => {
  return await ajaxPost(`/ironview360/hotspots/${hotspotId}/overlay-groups/`, data);
};

const getLayoutHotspots = async (layoutId: string) => {
  const res = await GET(`/layouts/${layoutId}/hotspots/`);
  const data = res?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: unknown[] }).results;
  }
  return [];
};

const getPaginatedHotspots = async (
  projectId: string,
  page: number = 1,
  pageSize: number = 20,
  search?: string
) => {
  const params = new URLSearchParams({
    project_id: projectId,
    paginate: 'true',
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (search?.trim()) {
    params.set('search', search.trim());
  }
  return (await GET(`/hotspots/?${params.toString()}`)).data;
};

export {
  getLayouts,
  getAreaTags,
  getPhotoMarkers,
  getAvailablePhotos,
  getPhoto360,
  getPhotoHotspots,
  getHotspotOverlays,
  createAreaTag,
  savePhotoMarkers,
  createHotspot,
  createNote,
  updateNote,
  deleteNote,
  getNotes,
  createNoteAttachment,
  getProjectUsers,
  getNote,
  uploadOverlay,
  deleteOverlay,
  deleteHotspot,
  updateHotspot,
  updateOverlay,
  createLabel,
  getLabels,
  uploadOverlayToHotspot,
  getLayoutHotspots,
  getPaginatedHotspots,
  getHotspotOverlayGroups,
  createHotspotOverlayGroup,
};
